import React from 'react';

import { cn } from '@/renderer/utils/cn';
import { useDebounce } from '@/renderer/utils/hooks/useDebounce';

// biome-ignore lint/correctness/noConstantCondition: Stupid React 18!
const inert = { inert: true ? '' : undefined };

type Div = React.ElementRef<'div' | 'span'>;

interface SkeletonProps<T extends React.ElementType = React.ElementType>
  extends React.HTMLAttributes<T> {
  loading?: boolean;
  as?: T;
  debounce?: number;
}

export const SKELETON_CLASSNAME_BASE = `pointer-events-none box-border animate-pulse cursor-default select-none rounded-sm border-0 text-transparent leading-[1em] shadow-none outline-hidden backdrop-invert-[.1] before:invisible after:invisible empty:h-3 *:invisible`;
export const SKELETON_CLASSNAME_WRAP = `overflow-hidden whitespace-nowrap`;
export const SKELETON_CLASSNAME_BLOCK = `empty:block`;

const Skeleton = React.forwardRef<Div, SkeletonProps>(function SkeletonInner(
  { children, className, loading, debounce = 200, as: As, ...skeletonProps },
  forwardedRef,
) {
  const isLoadingDebounced = useDebounce(loading, loading ? debounce / 2 : debounce);
  const isLoading = debounce > 0 ? isLoadingDebounced : loading;

  if (!isLoading) {
    return children;
  }

  const isValid = React.isValidElement(children);
  const Tag = As ? As : isValid ? 'div' : 'span';

  return (
    <Tag
      ref={forwardedRef}
      aria-hidden
      className={cn(
        SKELETON_CLASSNAME_BASE,
        SKELETON_CLASSNAME_WRAP,
        SKELETON_CLASSNAME_BLOCK,
        // Tag === 'span' && 'z-1 inline-block align-middle',
        Tag === 'span' && 'z-1',
        className,
      )}
      data-inline-skeleton={isValid ? undefined : true}
      tabIndex={-1}
      // Workaround to use `inert` until https://github.com/facebook/react/pull/24730 is merged.
      {...inert}
      {...skeletonProps}
    >
      {children}
    </Tag>
  );
});

export default Skeleton;
